/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { HighContrastSelectorBlack, HighContrastSelectorWhite } from '@fluentui/style-utilities';
import * as React from 'react';
import { getStylistV2, important, joinClassNames } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';

const { getClassName } = getStylistV2('BrandLogo');

const microsoftLogoWhiteClassName = getClassName('microsoftLogoWhite', {
    $nest: {
        [HighContrastSelectorBlack]: important({ display: 'inline-block' }),
        [HighContrastSelectorWhite]: important({ display: 'none' })
    }
});

const microsoftLogoBlackClassName = getClassName('microsoftLogoBlack', {
    $nest: {
        [HighContrastSelectorBlack]: important({ display: 'none' }),
        [HighContrastSelectorWhite]: important({ display: 'inline-block' })
    }
});

/**
 * Props for brand logo.
 * defaultLogo: when not in contrast mode, which logo should be shown.
 * whiteLogoBlobPath: path to the white logo on our blob storage.
 * blackLogoBlobPath: path to the black logo on our blob storage.
 * className (optional): enables styled components.
 * alt (optional): alt text for the logos.
 */
export type BrandLogoProps = {
    defaultLogo: 'whiteLogo' | 'blackLogo';
    whiteLogoBlobPath: string;
    blackLogoBlobPath: string;
    className?: string;
    alt?: string;
};

/**
 * Displays provided branding logos based on the hight contrast mode.
 */
export const BrandLogo = (props: BrandLogoProps) => (
    <>
        <img
            style={{ display: props.defaultLogo === 'whiteLogo' ? 'inline-block' : 'none' }}
            alt={props.alt}
            className={joinClassNames(props.className, microsoftLogoWhiteClassName)}
            src={getAssetsUrl(props.whiteLogoBlobPath)}
        />
        <img
            style={{ display: props.defaultLogo === 'blackLogo' ? 'inline-block' : 'none' }}
            alt={props.alt}
            className={joinClassNames(props.className, microsoftLogoBlackClassName)}
            src={getAssetsUrl(props.blackLogoBlobPath)}
        />
    </>
);
